import { IStore } from 'redux/IStore';
import { IPage, IKeyValuePair } from 'models/common';
import * as i18n from 'i18next';
import { actions as notifActions } from 'redux-notifications';
import { nullCheck } from 'helpers/common';
import { IMenuItemsAction, IMenuItemsData } from 'models/menuitem/menuitems';
import { IMenuItem } from 'models/menuitem/menuitem';
import { MenuItemStatus } from 'models/enums/statuses';
import { MenuItemType } from '../../../../models/enums/menuitemtype';
const { notifSend } = notifActions;

/** Action Types */
export const REQUEST_START: string = 'menuitems/REQUEST_START';
export const REQUEST_FAILURE: string = 'menuitems/REQUEST_FAILURE';
export const GET_MENU_ITEMS_SUCCESS: string = 'menuitems/GET_MENU_ITEMS_SUCCESS';
export const RESET_DATA: string = 'menuitems/RESET_DATA';
export const SET_ITEMS_PAGE: string = 'menuitems/SET_ITEMS_PAGE';
export const SET_STATUS_FILTER: string = 'menuitems/SET_STATUS_FILTER';
export const SET_LANGUAGE_FILTER: string = 'menuitems/SET_LANGUAGE_FILTER';
export const SET_SEARCH_FILTER: string = 'menuitems/SET_SEARCH_FILTER';
export const SET_SORT_FILTER: string = 'menuitems/SET_SORT_FILTER';
export const SET_ISFIRSTLOAD: string = 'menuitems/SET_ISFIRSTLOAD';
export const RESET_FILTERS: string = 'menuitems/RESET_FILTERS';

/** Initial State */
const initialState: IMenuItemsData = {
	items: [],
	itemsPage: {
		currentPage: 0,
		totalPages: 0,
		totalItems: 0,
		itemsPerPage: Number.MAX_SAFE_INTEGER,
		items: [],
	},
	keywordFilter: '',
	sortFilter: 0,
	statusFilter: 'allpages',
	languageKeyFilter: '',
	isFetching: false,
	isFirstLoad: false,
};

/** Reducer */
export function menuItemsReducer(state = initialState, action: IMenuItemsAction) {
	switch (action.type) {
		case REQUEST_START:
			return Object.assign({}, state, {
				isFetching: true,
				error: false,
			});

		case GET_MENU_ITEMS_SUCCESS:
			return Object.assign({}, state, {
				items: action.payload.items,
				isFetching: false,
			});

		case REQUEST_FAILURE:
			return Object.assign({}, state, {
				isFetching: false,
				message: action.payload.message,
				error: true,
			});

		case RESET_DATA:
			return Object.assign({}, state, {
				items: [],
				itemsPage: {
					currentPage: 0,
					totalPages: 0,
					totalItems: 0,
					itemsPerPage: Number.MAX_SAFE_INTEGER,
					items: [],
				},
			});

		case SET_ITEMS_PAGE:
			return Object.assign({}, state, {
				itemsPage: action.payload.itemsPage,
			});

		case SET_STATUS_FILTER:
			return Object.assign({}, state, {
				keywordFilter: '',
				sortFilter: 0,
				languageKeyFilter: '',
				statusFilter: action.payload.statusFilter,
				isFirstLoad: true,
			});

		case SET_LANGUAGE_FILTER:
			return Object.assign({}, state, {
				languageKeyFilter: action.payload.languageKeyFilter,
				isFirstLoad: true,
			});

		case SET_SORT_FILTER:
			return Object.assign({}, state, {
				sortFilter: action.payload.sortFilter,
				isFirstLoad: true,
			});

		case SET_SEARCH_FILTER:
			return Object.assign({}, state, {
				keywordFilter: action.payload.keywordFilter,
				isFirstLoad: true,
			});

		case SET_ISFIRSTLOAD:
			return Object.assign({}, state, {
				isFirstLoad: action.payload.isFirstLoad,
			});

		default:
			return state;
	}
}

/** Action Creator */
export function getMenuItems(menuItemType: MenuItemType) {
	return (dispatch, getState: () => IStore) => {
		if (getState().menuItems.isFirstLoad) {
			dispatch(resetData());
			dispatch(setIsFirstLoad(false));
		}

		dispatch(requestStart());

		let url = getState().fetchDefault.baseAPI + 'api/MenuItem/';

		if (menuItemType === MenuItemType.Portal) {
			url = url + 'Portal/Search';
		} else if (menuItemType === MenuItemType.Magazine) {
			url = url + 'Magazine/Search';
		}

		const currentPage: number = getState().menuItems.itemsPage.currentPage;
		const newPage: number = currentPage + 1;
		const itemsPerPage: number = getState().menuItems.itemsPage.itemsPerPage;

		let queryString = '?';
		if (getState().menuItems.keywordFilter && getState().menuItems.keywordFilter !== '') {
			queryString = queryString + '&keyword=' + getState().menuItems.keywordFilter;
		}

		if (getState().menuItems.languageKeyFilter) {
			queryString = queryString + '&languageKey=' + getState().menuItems.languageKeyFilter;
		}

		queryString = queryString + '&currentPage=' + newPage;
		queryString = queryString + '&itemsPerPage=' + itemsPerPage;
		queryString = queryString + '&sortColumn=' + mapAdminMenuItemsSort(getState().menuItems.sortFilter);
		if (nullCheck(mapAdminMenuItemsStatus(getState().menuItems.statusFilter))) {
			queryString = queryString + '&status=' + mapAdminMenuItemsStatus(getState().menuItems.statusFilter);
		}

		return fetch(
			url + queryString,
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'GET',
			}),
		)
			.then((res) => {
				if (res.ok) {
					return res.json().then((res) => {
						const menuItemsState: IMenuItemsData = getState().menuItems;
						const items: IMenuItem[] = menuItemsState.items;
						const newItems: IMenuItem[] = res.items;
						Array.prototype.push.apply(items, newItems);
						items.map((item: IMenuItem) => {
							item.pageJObject = JSON.parse(item.page);
						});
						dispatch(getMenuItemsSuccess(items));
						dispatch(setItemsPage(res));
					});
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Async Action Creator */
export function deleteMenuItem(itemId: string) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());
		return fetch(
			getState().fetchDefault.baseAPI + 'api/MenuItem/' + itemId,
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'DELETE',
			}),
		)
			.then((res) => {
				if (res.ok) {
					dispatch(deleteMenuItemFromList(itemId));
					dispatch(
						notifSend({
							message: i18n.t('Notification_Success_MenuItem_Deleted'),
							kind: 'success',
							dismissAfter: 3000,
						}),
					);
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator */
export function publishMenuItem(itemId: string, languageKey: number) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		const menuItemToPublish: IKeyValuePair = {
			key: itemId,
			value: languageKey,
		};

		return fetch(
			getState().fetchDefault.baseAPI + 'api/MenuItem/Publish',
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'POST',
				body: JSON.stringify(menuItemToPublish),
			}),
		)
			.then((res) => {
				if (res.ok) {
					dispatch(updateMenuItemToPublished(itemId, languageKey));
					dispatch(
						notifSend({
							message: i18n.t('Notification_Success_LandingPage_Published'),
							kind: 'success',
							dismissAfter: 3000,
						}),
					);
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator */
export function updateMenuItemToPublished(itemId: string, languageKey: number) {
	return (dispatch, getState: () => IStore) => {
		const items: IMenuItem[] = getState().menuItems.items;
		const indexToUnpublish = items.indexOf(
			items.find((x: IMenuItem) => x.languageKey === languageKey && x.published === true),
		);
		const indexToPublish = items.indexOf(items.find((x: IMenuItem) => x.id === itemId));
		items[indexToPublish].published = true;
		items[indexToUnpublish].published = false;
		dispatch(getMenuItemsSuccess(items));
	};
}

/** Action Creator */
export function deleteMenuItemFromList(itemId: string) {
	return (dispatch, getState: () => IStore) => {
		const items: IMenuItem[] = getState().menuItems.items;
		const index = items.indexOf(items.find((x: IMenuItem) => x.id === itemId));
		items.splice(index, 1);
		dispatch(getMenuItemsSuccess(items));
	};
}

/** Action Creator */
export function updateMenuItemInList(menuItem: IMenuItem) {
	return (dispatch, getState: () => IStore) => {
		const items: IMenuItem[] = getState().menuItems.items;
		const itemToUpdate: IMenuItem = items.find((x) => x.id === menuItem.id);
		const index = items.indexOf(items.find((x: IMenuItem) => x.id === menuItem.id));
		itemToUpdate.title = menuItem.title;
		items[index] = itemToUpdate;
		dispatch(getMenuItemsSuccess(items));
	};
}

function mapAdminMenuItemsStatus(status: string) {
	let mappedStatus = null;
	switch (status) {
		case 'allpages':
			mappedStatus = null;
			break;
		case 'active':
			mappedStatus = MenuItemStatus.Active;
			break;
		case 'expired':
			mappedStatus = MenuItemStatus.Expired;
			break;

		default:
			mappedStatus = null;
	}
	return mappedStatus;
}

function mapAdminMenuItemsSort(sort: number) {
	let mappedSort = 'ReleaseDate DESC';
	switch (sort) {
		case 0:
			mappedSort = 'ReleaseDate DESC';
			break;
		case 1:
			mappedSort = 'ReleaseDate ASC';
			break;
		case 2:
			mappedSort = 'ExpireDate DESC';
			break;
		case 3:
			mappedSort = 'ExpireDate ASC';
			break;
		default:
			mappedSort = 'ReleaseDate DESC';
	}
	return mappedSort;
}

/** Action Creator */
export function requestStart(): IMenuItemsAction {
	return {
		type: REQUEST_START,
	};
}

/** Action Creator */
export function getMenuItemsSuccess(items: IMenuItem[]): IMenuItemsAction {
	return {
		type: GET_MENU_ITEMS_SUCCESS,
		payload: {
			items,
		},
	};
}

/** Action Creator */
export function requestFailure(message: any): IMenuItemsAction {
	return {
		type: REQUEST_FAILURE,
		payload: {
			message,
		},
	};
}

/** Action Creator */
export function setItemsPage(itemsPage: IPage): IMenuItemsAction {
	return {
		type: SET_ITEMS_PAGE,
		payload: {
			itemsPage,
		},
	};
}

/** Action Creator */
export function resetData(): IMenuItemsAction {
	return {
		type: RESET_DATA,
	};
}

export function setStatusFilter(statusFilter: string): IMenuItemsAction {
	return {
		type: SET_STATUS_FILTER,
		payload: {
			statusFilter,
		},
	};
}

export function setLanguageFilter(languageKeyFilter: string): IMenuItemsAction {
	return {
		type: SET_LANGUAGE_FILTER,
		payload: {
			languageKeyFilter,
		},
	};
}

export function setSearchFilter(keywordFilter: string): IMenuItemsAction {
	return {
		type: SET_SEARCH_FILTER,
		payload: {
			keywordFilter,
		},
	};
}

export function setSortFilter(sortFilter: number): IMenuItemsAction {
	return {
		type: SET_SORT_FILTER,
		payload: {
			sortFilter,
		},
	};
}

export function resetFilters(): IMenuItemsAction {
	return {
		type: RESET_FILTERS,
	};
}

export function setIsFirstLoad(isFirstLoad: boolean): IMenuItemsAction {
	return {
		type: SET_ISFIRSTLOAD,
		payload: {
			isFirstLoad,
		},
	};
}
